import { OrganizationId, ProjectId, ProjectTypeId } from '@kaizenplatform/hugot-entity';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import gql from 'graphql-tag';
import { flatMap } from 'lodash-es';

import GraphQLClient from '../../adapter/GraphQLClient';
import Config from '../../core/Config';
import { SearchAssetQuery, SearchAssetResult } from '../../entity/SearchAsset';

class SearchAssetApi {
  private readonly axios: AxiosInstance;
  private readonly searchAssetApiUrl: string;
  private readonly graphqlClient: GraphQLClient;
  private readonly hugotApi: string;

  constructor() {
    this.axios = axios.create({
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    });

    const config: Config = (window as any).__hugotconfig__;
    this.searchAssetApiUrl = config.SEARCH_ASSET_API_URL;
    this.graphqlClient = new GraphQLClient({ endpointUrl: config.HUGOT_API_GRAPHQL_ENDPOINT });
    this.hugotApi = config.HUGOT_API_BASE_URL;
  }

  /*
    利用規約モーダル
    GET: userが'今後このメッセージを表示しない'にチェックを入れているか取得
  */
  public getUserTermAgreements = async (userId: string, options: AxiosRequestConfig = {}): Promise<boolean> => {
    try {
      const response = await this.axios.get(
        `${this.hugotApi}/user_term_agreements?user_id=${userId}&user_type=Director&type=search_assets`,
        {
          ...options,
          withCredentials: true,
        },
      );
      return response.status === 200;
    } catch (error) {
      return false;
    }
  };

  /*
    利用規約モーダル
    POST: '今後このメッセージを表示しない'にチェックを入れたことをPOST
  */
  public postUserTermAgreements = async (options: AxiosRequestConfig = {}): Promise<void> => {
    const data = { user_type: 'Director', type: 'search_assets' };
    await this.axios.post(`${this.hugotApi}/user_term_agreements`, data, {
      ...options,
      withCredentials: true,
    });
  };

  public search = async (
    {
      region,
      service,
      type,
      query,
      page,
      projectId,
      projectTypeId,
      organizationId,
    }: SearchAssetQuery & { projectId?: ProjectId; projectTypeId?: ProjectTypeId; organizationId?: OrganizationId },
    options: AxiosRequestConfig = {},
  ): Promise<SearchAssetResult> => {
    const response = await this.axios.get(`${this.searchAssetApiUrl}/search`, {
      ...options,
      params: {
        region: region,
        service: service,
        type: type,
        query: query !== '' ? query : undefined,
        page: page != null ? page.toString() : undefined,
        project_id: projectId != null ? projectId : undefined,
        project_type_id: projectTypeId != null ? projectTypeId : undefined,
        organization_id: organizationId != null ? organizationId : undefined,
      },
    });

    return response.data.result as SearchAssetResult;
  };

  public getPreparedMaterialCategories = async (categoryIds: ReadonlyArray<number>): Promise<any> => {
    const result = await this.graphqlClient.query(
      gql`
        query getPreparedMaterialCategories($categoryIds: [ID!]!) {
          preparedMaterialCategories(preparedMaterialCategoryIds: $categoryIds) {
            id
            name
            type
            preparedMaterials {
              ... on VideoPreparedMaterial {
                id
                originalUrl
                lightWeightUrl
                thumbnailUrl
              }
              ... on ImagePreparedMaterial {
                id
                originalUrl
                lightWeightUrl
              }
              ... on AudioPreparedMaterial {
                id
                originalUrl
                displayName
              }
            }
          }
        }
      `,
      { categoryIds },
    );
    return flatMap(result.preparedMaterialCategories, (category) => {
      return category.preparedMaterials.map((material: any) => {
        return {
          type: category.type,
          originalUrl: `https:${material.originalUrl}`,
          lightWeightUrl: material.lightWeightUrl != null ? `https:${material.lightWeightUrl}` : undefined,
          thumbnailUrl: material.thumbnailUrl != null ? `https:${material.thumbnailUrl}` : undefined,
          displayName: material.displayName != null ? material.displayName : undefined,
          category: category.name,
        };
      });
    });
  };
}

export default SearchAssetApi;
